import { Period, PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import sub from 'date-fns/sub'
import isEqual from 'lodash/isEqual'
import Link from 'next/link'
import { type FC, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled, { useTheme } from 'styled-components'

import Logo from 'assets/svg/brand/logo-only.svg'
import Button from 'components/Button'
import { MobileHiddenView, MobileOnlyView } from 'components/Media'
import SegmentedControl from 'components/SegmentedControl'
import Spacer from 'components/Spacer'
import { Body, Heading } from 'components/Text'
import { NumberBody } from 'components/Text/NumericValue'
import { FlexDivRowCentered } from 'components/layout/flex'
import { GridDivCenteredRow } from 'components/layout/grid'
import { DASHBOARD_SECTION_HEIGHT } from 'constants/ui'
import {
	selectPerpsProvider,
	selectPortfolioChartType,
	selectSelectedPortfolioTimeframe,
} from 'state/futures/common/selectors'
import { setPreferences } from 'state/futures/reducer'
import { selectFuturesPortfolio, selectPortfolioChartData } from 'state/futures/selectors'
import type { PortfolioValues } from 'state/futures/snxPerpsV3/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import { formatChartDate, formatChartTime, formatShortDateWithTime } from 'utils/dates'
import { Timeframe } from './Timeframe'

type ChartType = 'account' | 'pnl'
type PriceChartProps = {
	chartType: ChartType
	data: PortfolioValues[]
}

const ChartCTA = () => {
	const { t } = useTranslation()
	const marketPageRoute = useMarketPageRoute()

	return (
		<CTAContainer>
			<Logo />
			<Heading variant="h3">{t('dashboard.overview.portfolio-chart.welcome')}</Heading>
			<Body color={'secondary'}>{t('dashboard.overview.portfolio-chart.hero')}</Body>
			<Link href={marketPageRoute()}>
				<Button variant="flat" size="medium">
					{t('homepage.nav.trade-now')}
				</Button>
			</Link>
		</CTAContainer>
	)
}

const CustomTooltip = ({
	chartType,
	active,
	payload,
}: {
	chartType: ChartType
	active?: boolean
	payload?: { payload?: { timestamp: number; total: number } }[]
}) => {
	if (active && payload && payload[0]?.payload) {
		return (
			<TooltipContainer>
				<Body>{formatShortDateWithTime(payload[0].payload?.timestamp)}</Body>
				<Spacer height={14} />
				<FlexDivRowCentered justifyContent="flex-begin">
					<TooltipDot />
					<Spacer width={5} />
					<Body color="secondary">{chartType === 'account' ? 'Account value' : 'P&L'}</Body>
				</FlexDivRowCentered>
				<Spacer height={3} />
				<NumberBody weight="bold" size="large">
					{formatDollars(payload[0].payload?.total)}
				</NumberBody>
			</TooltipContainer>
		)
	}

	return null
}

const PriceChart: FC<PriceChartProps> = ({ data, chartType }) => {
	const theme = useTheme()
	const portfolioTimeframe = useAppSelector(selectSelectedPortfolioTimeframe)

	const dataPoints = useMemo(() => {
		if (data.length) {
			return data
		}

		const minX = sub(new Date(), {
			years: portfolioTimeframe === Period.ONE_YEAR ? 1 : 0,
			months: portfolioTimeframe === Period.ONE_MONTH ? 1 : 0,
			weeks: portfolioTimeframe === Period.ONE_WEEK ? 1 : 0,
		}).getTime()

		return [
			{ timestamp: minX, total: 0 },
			{ timestamp: new Date().getTime(), total: 0 },
		]
	}, [data, portfolioTimeframe])

	let max = [...dataPoints].sort((a, b) => b.total - a.total)[0]?.total ?? 0
	let min = [...dataPoints].sort((a, b) => a.total - b.total)[0]?.total ?? 0
	max = max > 0 ? max + max * 0.1 : max - max * 0.1
	min = min > 0 ? min - min * 0.1 : min + min * 0.1

	return (
		<ResponsiveContainer width="100%" height="100%">
			<AreaChart data={dataPoints} margin={{ top: 12, bottom: 0, left: 8, right: 0 }}>
				<defs>
					<linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
						<stop
							offset="5%"
							stopColor={theme.colors.selectedTheme.newTheme.text.preview}
							stopOpacity={0.14}
						/>
						<stop
							offset="95%"
							stopColor={theme.colors.selectedTheme.newTheme.text.preview}
							stopOpacity={0}
						/>
					</linearGradient>
				</defs>
				<XAxis
					dataKey="timestamp"
					type="number"
					scale="time"
					minTickGap={75}
					color={theme.colors.selectedTheme.newTheme.text.secondary}
					tickFormatter={portfolioTimeframe === Period.ONE_WEEK ? formatChartTime : formatChartDate}
					axisLine={{ stroke: theme.colors.selectedTheme.newTheme.border.color }}
					tickLine={{ stroke: undefined }}
					domain={['dataMin', 'dataMax']}
				/>
				<YAxis
					dataKey="total"
					type="number"
					color={theme.colors.selectedTheme.newTheme.text.secondary}
					tickFormatter={(value) => formatDollars(wei(value), { suggestDecimals: true })}
					axisLine={{ stroke: undefined }}
					tickLine={{ stroke: undefined }}
					domain={[min, max]}
				/>
				<Tooltip content={(props) => <CustomTooltip {...props} chartType={chartType} />} />
				<Area
					type="monotone"
					dataKey="total"
					stroke={theme.colors.selectedTheme.newTheme.text.preview}
					strokeWidth={2}
					fillOpacity={1}
					fill="url(#colorUv)"
				/>
			</AreaChart>
		</ResponsiveContainer>
	)
}

const PortfolioChart: FC = () => {
	const dispatch = useAppDispatch()
	const { t } = useTranslation()
	const { crossMargin: crossTotal, smartMargin: smartTotal } =
		useAppSelector(selectFuturesPortfolio)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const portfolioData = useAppSelector(selectPortfolioChartData, isEqual)

	const chartType = useAppSelector(selectPortfolioChartType)

	const total = useMemo(
		() => (providerIsCrossMargin(perpsProvider) ? crossTotal : smartTotal),
		[perpsProvider, crossTotal, smartTotal]
	)

	const onToggleChartType = useCallback(
		(index: number) => {
			dispatch(setPreferences({ portfolioChartType: index === 0 ? 'account' : 'pnl' }))
		},
		[dispatch]
	)

	useEffect(() => {
		if (perpsProvider === PerpsProvider.SNX_V2_OP && chartType !== 'account') {
			dispatch(setPreferences({ portfolioChartType: 'account' }))
		}
	}, [perpsProvider, chartType, dispatch])

	const chartTypeSelection = useMemo(() => {
		if (providerIsCrossMargin(perpsProvider)) {
			return (
				<SegmentedControl
					width={'160px'}
					onChange={onToggleChartType}
					values={['Account', 'P&L']}
					selectedIndex={chartType === 'account' ? 0 : 1}
				/>
			)
		}
		if (perpsProvider === PerpsProvider.SNX_V2_OP) {
			return <Body weight="bold">{t('dashboard.overview.portfolio-chart.account')}</Body>
		}
	}, [perpsProvider, chartType, onToggleChartType, t])

	return (
		<>
			<MobileHiddenView>
				<ChartGrid>
					{total ? (
						<ChartContainer>
							<TopBar>
								{chartTypeSelection}
								<TimeframeOverlay>
									<Timeframe />
								</TimeframeOverlay>
							</TopBar>
							<PriceChart chartType={chartType} data={portfolioData} />
						</ChartContainer>
					) : (
						<ChartContainer>
							<ChartCTA />
						</ChartContainer>
					)}
				</ChartGrid>
			</MobileHiddenView>
			<MobileOnlyView>
				{total ? (
					<MobileChartGrid>
						<ChartContainer>
							<TopBar>
								<TimeframeOverlay>
									<Timeframe />
								</TimeframeOverlay>
							</TopBar>
							<PriceChart chartType={chartType} data={portfolioData} />
						</ChartContainer>
					</MobileChartGrid>
				) : (
					<ChartContainer mobile>
						<ChartCTA />
					</ChartContainer>
				)}
			</MobileOnlyView>
		</>
	)
}

const ChartContainer = styled.div<{ mobile?: boolean }>`
	position: relative;
	grid-row-end: span 3;
	border-top: ${(props) => (props.mobile ? props.theme.colors.selectedTheme.border : null)};
	border-bottom: ${(props) => (props.mobile ? props.theme.colors.selectedTheme.border : null)};
	padding-left: 10px;
	padding-right: 20px;
	padding-top: 70px;
	height: ${DASHBOARD_SECTION_HEIGHT - 20}px;
`

const ChartGrid = styled.div`
	width: 100%;
	height: 100%;
`

const TopBar = styled.div`
	position: absolute;
	top: 20px;
	right: 20px;
	left: 20px;
	z-index: 3;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-start;
`

const TimeframeOverlay = styled.div`
	max-width: 192px;
`

const MobileChartGrid = styled.div`
	display: grid;
	grid-template-rows: 1fr 5fr;
	width: 100%;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 0px;
	height: 360px;
`

const CTAContainer = styled(GridDivCenteredRow)`
	height: 100%;
	text-align: center;
	justify-content: center;
	justify-items: center;
	grid-gap: 10px;
	padding: 50px 40px;
	margin-top: -2px;
`

const TooltipContainer = styled.div`
	background: ${(props) => props.theme.colors.selectedTheme.background};
	border: 1px solid #e5e5e5;
	padding: 18px;
	border-radius: 8px;
	border: ${(props) => props.theme.colors.selectedTheme.border};
`

const TooltipDot = styled.div`
	width: 8px;
	height: 8px;
	border-radius: 100%;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
`

export default PortfolioChart
